import { CONTENT } from "../shared/content";
import { animationFrameAt } from "../shared/animation";
import type { CharacterDefinition } from "../shared/types";
import { registerLiveAudioContext } from "./audioLifetime";
import { mixedVolume, subscribeAudioSettings } from "./audioSettings";
import { GAMEPAD, connectedGamepad, gamepadButtonDown, readGamepadMove, risingEdge } from "./gamepad";
import { menuAxisPulse, type MenuAxis } from "./menuNav";
import {
  EQ_BANDS,
  EQ_PRESETS,
  SOUNDTRACK,
  formatEqLabel,
  formatTrackTime,
  nextTrackIndex,
  previousTrackIndex,
  trackIndexAfterEnd,
  type RepeatMode
} from "./soundtrack";
import "./winamp.css";

const PRESET_NAMES = Object.keys(EQ_PRESETS);
const FRAME = 144;
const COLUMNS = 8;

let open = false;

export function isSoundtrackOpen(): boolean {
  return open;
}

export function bindWinamp(options: { onOpen?: () => void; onClose?: () => void } = {}): void {
  const desktop = document.querySelector<HTMLElement>("#winamp");
  if (!desktop) throw new Error("The soundtrack player is missing.");

  const audio = document.createElement("audio");
  audio.preload = "auto";
  audio.dataset.soundtrack = "winamp";
  document.body.append(audio);

  const scope = required<HTMLCanvasElement>("#wa-scope");
  const milk = required<HTMLCanvasElement>("#wa-milk");
  const ticker = required<HTMLElement>("#wa-ticker");
  const tickerText = required<HTMLElement>("#wa-ticker-text");
  const kbps = required<HTMLElement>("#wa-kbps");
  const seek = required<HTMLInputElement>("#wa-seek");
  const playlist = required<HTMLElement>("#wa-playlist");
  const clock = required<HTMLElement>("#wa-clock");
  const bands = required<HTMLElement>("#wa-bands");
  const preampSlot = required<HTMLElement>("#wa-preamp");
  const fighterName = required<HTMLElement>("#wa-fighter-name");
  const eqWindow = required<HTMLElement>("#wa-eq");
  const playlistWindow = required<HTMLElement>("#wa-playlist-window");

  const buttons = {
    play: required<HTMLButtonElement>("#wa-play"),
    pause: required<HTMLButtonElement>("#wa-pause"),
    stop: required<HTMLButtonElement>("#wa-stop"),
    prev: required<HTMLButtonElement>("#wa-prev"),
    next: required<HTMLButtonElement>("#wa-next"),
    shuffle: required<HTMLButtonElement>("#wa-shuffle"),
    repeat: required<HTMLButtonElement>("#wa-repeat"),
    eq: required<HTMLButtonElement>("#wa-toggle-eq"),
    pl: required<HTMLButtonElement>("#wa-toggle-pl"),
    eqOn: required<HTMLButtonElement>("#wa-eq-on"),
    eqAuto: required<HTMLButtonElement>("#wa-eq-auto"),
    presets: required<HTMLButtonElement>("#wa-presets")
  };

  let trackIndex = 0;
  let shuffle = false;
  let repeat: RepeatMode = "all";
  let eqOn = true;
  let volume = 0.8;
  let presetIndex = 0;
  let seeking = false;
  let graph: AudioGraph | null = null;
  let frame = 0;
  let characterStarted = 0;
  let axisX: MenuAxis = 0;
  let confirmDown = false;
  let backDown = false;
  const flatPreset = EQ_PRESETS.Flat ?? EQ_BANDS.map(() => 0);
  const gains = flatPreset.map(() => 0);
  let preampDb = 0;
  const sprites = new Map<string, HTMLImageElement>();

  const preamp = createFader(preampSlot, "Preamp", () => {
    preampDb = readFader(preamp);
    applyEq();
  });
  const faders = EQ_BANDS.map((hz, index) =>
    createFader(bands, formatEqLabel(hz), () => {
      gains[index] = readFader(faders[index]);
      applyEq();
    })
  );

  for (const track of SOUNDTRACK) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "wa-track";
    row.innerHTML = `<span class="wa-num"></span><span class="wa-title"></span><span class="wa-time"></span>`;
    const title = row.querySelector(".wa-title");
    const time = row.querySelector(".wa-time");
    const num = row.querySelector(".wa-num");
    if (num) num.textContent = `${SOUNDTRACK.indexOf(track) + 1}.`;
    if (title) title.textContent = track.title;
    if (time) time.textContent = formatTrackTime(track.seconds);
    row.addEventListener("click", () => {
      trackIndex = SOUNDTRACK.indexOf(track);
      void playCurrent(true);
    });
    playlist.append(row);
  }

  const paintTransport = (): void => {
    buttons.play.classList.toggle("is-on", !audio.paused && !audio.ended);
    buttons.pause.classList.toggle("is-on", audio.paused && audio.currentTime > 0);
    buttons.shuffle.classList.toggle("is-on", shuffle);
    buttons.repeat.classList.toggle("is-on", repeat !== "off");
    buttons.repeat.dataset.mode = repeat;
    buttons.eq.classList.toggle("is-on", !eqWindow.hidden);
    buttons.pl.classList.toggle("is-on", !playlistWindow.hidden);
    buttons.eqOn.classList.toggle("is-on", eqOn);
    const rows = [...playlist.querySelectorAll<HTMLButtonElement>(".wa-track")];
    for (const [index, row] of rows.entries()) row.classList.toggle("is-current", index === trackIndex);
  };

  const paintReadout = (): void => {
    const track = SOUNDTRACK[trackIndex];
    if (!track) return;
    const elapsed = formatTrackTime(audio.currentTime || 0);
    const duration = formatTrackTime(Number.isFinite(audio.duration) ? audio.duration : track.seconds);
    tickerText.textContent = `${track.stage.toUpperCase()} - ${track.title.toUpperCase()} <${elapsed}>`;
    kbps.textContent = String(track.kbps);
    clock.textContent = `${elapsed}/${duration}`;
    if (!seeking && Number.isFinite(audio.duration) && audio.duration > 0) {
      seek.value = String(Math.round((audio.currentTime / audio.duration) * 1000));
    }
    const overflow = tickerText.scrollWidth > ticker.clientWidth;
    tickerText.classList.toggle("is-scrolling", overflow);
  };

  const outputGain = (): number => mixedVolume("music", volume * 0.9);

  const ensureGraph = (): AudioGraph => {
    if (graph && graph.context.state !== "closed") return graph;
    const context = new AudioContext();
    registerLiveAudioContext(context);
    const source = context.createMediaElementSource(audio);
    const pre = context.createGain();
    const level = context.createGain();
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    const filters = EQ_BANDS.map((hz) => {
      const filter = context.createBiquadFilter();
      filter.type = "peaking";
      filter.frequency.value = hz;
      filter.Q.value = 1.2;
      return filter;
    });
    let node: AudioNode = source;
    node.connect(pre);
    node = pre;
    for (const filter of filters) {
      node.connect(filter);
      node = filter;
    }
    node.connect(level);
    level.connect(analyser);
    analyser.connect(context.destination);
    graph = { context, pre, level, analyser, filters };
    applyEq();
    return graph;
  };

  const applyEq = (): void => {
    if (!graph) return;
    graph.pre.gain.value = 10 ** (preampDb / 20);
    graph.level.gain.value = outputGain();
    for (const [index, filter] of graph.filters.entries()) {
      filter.gain.value = eqOn ? (gains[index] ?? 0) : 0;
    }
  };

  const load = (index: number): void => {
    const track = SOUNDTRACK[index];
    if (!track) return;
    trackIndex = index;
    if (!audio.src.endsWith(track.url) && !audio.src.includes(track.url)) audio.src = track.url;
    paintTransport();
    paintReadout();
  };

  const playCurrent = async (fromStart: boolean): Promise<void> => {
    const chain = ensureGraph();
    if (chain.context.state === "suspended") await chain.context.resume();
    load(trackIndex);
    if (fromStart) audio.currentTime = 0;
    applyEq();
    try {
      await audio.play();
    } catch {
      // Autoplay can still reject if the opener gesture was lost.
    }
    paintTransport();
  };

  const openPlayer = (opener: HTMLElement | null): void => {
    if (open) return;
    open = true;
    desktop.hidden = false;
    desktop.dataset.opener = opener?.id ?? "";
    options.onOpen?.();
    characterStarted = performance.now();
    if (!frame) frame = requestAnimationFrame(draw);
    void playCurrent(audio.currentTime <= 0 && audio.paused);
    buttons.play.focus();
  };

  const closePlayer = (): void => {
    if (!open) return;
    open = false;
    audio.pause();
    desktop.hidden = true;
    cancelAnimationFrame(frame);
    frame = 0;
    options.onClose?.();
    const opener = document.getElementById(desktop.dataset.opener ?? "");
    opener?.focus();
  };

  const step = (direction: -1 | 1): void => {
    const count = SOUNDTRACK.length;
    trackIndex =
      direction < 0
        ? previousTrackIndex(trackIndex, count, shuffle)
        : nextTrackIndex(trackIndex, count, shuffle);
    void playCurrent(true);
  };

  buttons.play.addEventListener("click", () => void playCurrent(false));
  buttons.pause.addEventListener("click", () => {
    audio.pause();
    paintTransport();
  });
  buttons.stop.addEventListener("click", () => {
    audio.pause();
    audio.currentTime = 0;
    paintTransport();
    paintReadout();
  });
  buttons.prev.addEventListener("click", () => {
    if (audio.currentTime > 2) {
      audio.currentTime = 0;
      return;
    }
    step(-1);
  });
  buttons.next.addEventListener("click", () => step(1));
  buttons.shuffle.addEventListener("click", () => {
    shuffle = !shuffle;
    paintTransport();
  });
  buttons.repeat.addEventListener("click", () => {
    repeat = repeat === "off" ? "all" : repeat === "all" ? "one" : "off";
    paintTransport();
  });
  const hideWindow = (window: HTMLElement): void => {
    window.hidden = true;
    paintTransport();
  };
  buttons.eq.addEventListener("click", () => {
    eqWindow.hidden = !eqWindow.hidden;
    paintTransport();
  });
  buttons.pl.addEventListener("click", () => {
    playlistWindow.hidden = !playlistWindow.hidden;
    paintTransport();
  });
  document.querySelector("#wa-eq-hide")?.addEventListener("click", () => hideWindow(eqWindow));
  document.querySelector("#wa-pl-hide")?.addEventListener("click", () => hideWindow(playlistWindow));
  buttons.eqOn.addEventListener("click", () => {
    eqOn = !eqOn;
    applyEq();
    paintTransport();
  });
  buttons.eqAuto.addEventListener("click", () => {
    const dance = EQ_PRESETS.Dance ?? flatPreset;
    const enabled = !buttons.eqAuto.classList.contains("is-on");
    buttons.eqAuto.classList.toggle("is-on", enabled);
    writeGains(enabled ? dance : gains.map(() => 0));
  });
  buttons.presets.addEventListener("click", () => {
    presetIndex = (presetIndex + 1) % PRESET_NAMES.length;
    const name = PRESET_NAMES[presetIndex] ?? "Flat";
    buttons.presets.textContent = name.toUpperCase();
    writeGains(EQ_PRESETS[name] ?? flatPreset);
  });

  for (const mini of desktop.querySelectorAll<HTMLButtonElement>("[data-wa]")) {
    mini.addEventListener("click", () => {
      const action = mini.dataset.wa;
      if (action === "prev") buttons.prev.click();
      if (action === "play") buttons.play.click();
      if (action === "pause") buttons.pause.click();
      if (action === "stop") buttons.stop.click();
      if (action === "next") buttons.next.click();
    });
  }

  seek.addEventListener("pointerdown", () => {
    seeking = true;
  });
  seek.addEventListener("input", () => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    audio.currentTime = (Number(seek.value) / 1000) * audio.duration;
    paintReadout();
  });
  const releaseSeek = (): void => {
    seeking = false;
  };
  seek.addEventListener("pointerup", releaseSeek);
  seek.addEventListener("change", releaseSeek);
  audio.addEventListener("timeupdate", paintReadout);
  audio.addEventListener("play", paintTransport);
  audio.addEventListener("pause", paintTransport);
  audio.addEventListener("ended", () => {
    const next = trackIndexAfterEnd(trackIndex, SOUNDTRACK.length, repeat, shuffle);
    if (next === null) {
      paintTransport();
      return;
    }
    trackIndex = next;
    void playCurrent(true);
  });

  const volumeSlider = required<HTMLInputElement>("#wa-volume");
  volumeSlider.addEventListener("input", () => {
    volume = Number(volumeSlider.value) / 100;
    applyEq();
  });
  subscribeAudioSettings(() => applyEq());

  for (const closer of desktop.querySelectorAll<HTMLButtonElement>("[data-wa-close]")) {
    closer.addEventListener("click", closePlayer);
  }
  for (const opener of document.querySelectorAll<HTMLButtonElement>("[data-open-soundtrack]")) {
    opener.addEventListener("click", () => openPlayer(opener));
  }
  document.addEventListener("keydown", (event) => {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closePlayer();
      return;
    }
    const typing = event.target instanceof HTMLInputElement && event.target.type === "range";
    if (typing) return;
    if (event.key === " ") {
      event.preventDefault();
      if (audio.paused) void playCurrent(false);
      else audio.pause();
    }
    if (event.key === "ArrowRight") step(1);
    if (event.key === "ArrowLeft") buttons.prev.click();
  });

  const pollPad = (): void => {
    if (open) {
      const pad = connectedGamepad();
      const move = readGamepadMove(pad);
      const dx = menuAxisPulse(axisX, move.x);
      axisX = move.x;
      const confirm = risingEdge(confirmDown, gamepadButtonDown(pad, GAMEPAD.A));
      const back = risingEdge(backDown, gamepadButtonDown(pad, GAMEPAD.B));
      confirmDown = confirm.down;
      backDown = back.down;
      if (dx < 0) buttons.prev.click();
      if (dx > 0) step(1);
      if (confirm.pressed) {
        if (audio.paused) void playCurrent(false);
        else audio.pause();
      }
      if (back.pressed) closePlayer();
    }
    requestAnimationFrame(pollPad);
  };
  requestAnimationFrame(pollPad);

  const roster = CONTENT.characters.filter((character) => character.spriteSheet);
  for (const character of roster) warmSprite(character, sprites);

  const scopeCtx = scope.getContext("2d");
  const milkCtx = milk.getContext("2d");
  const timeData = new Uint8Array(2048);
  const freqData = new Uint8Array(1024);

  const draw = (now: number): void => {
    frame = requestAnimationFrame(draw);
    if (!open) return;
    paintReadout();
    const chain = graph;
    if (chain) {
      chain.analyser.getByteTimeDomainData(timeData);
      chain.analyser.getByteFrequencyData(freqData);
    }
    if (scopeCtx) drawScope(scopeCtx, scope, timeData);
    if (milkCtx) {
      const character = drawMilk(milkCtx, milk, freqData, roster, sprites, now, characterStarted);
      if (character) fighterName.textContent = character.displayName.toUpperCase();
    }
  };

  function writeGains(next: number[]): void {
    for (const [index, value] of next.entries()) {
      gains[index] = value;
      setFader(faders[index], value);
    }
    eqOn = true;
    applyEq();
    paintTransport();
  }

  paintTransport();
  paintReadout();
  load(0);
}

function drawScope(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  time: Uint8Array
): void {
  fitCanvas(canvas);
  const { width, height } = canvas;
  ctx.fillStyle = "#070b12";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "#1c4a28";
  ctx.strokeRect(0.5, 0.5, width - 1, height - 1);
  ctx.beginPath();
  ctx.strokeStyle = "#3dff6a";
  ctx.lineWidth = 1.5;
  ctx.shadowColor = "#39ff6a";
  ctx.shadowBlur = 6;
  const step = Math.max(1, Math.floor(time.length / width));
  for (let x = 0; x < width; x += 1) {
    const sample = time[x * step] ?? 128;
    const y = (sample / 255) * (height - 8) + 4;
    if (x === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.shadowBlur = 0;
}

function drawMilk(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  freq: Uint8Array,
  roster: CharacterDefinition[],
  sprites: Map<string, HTMLImageElement>,
  now: number,
  started: number
): CharacterDefinition | undefined {
  fitCanvas(canvas);
  const { width, height } = canvas;
  const period = 6800;
  const index = roster.length ? Math.floor((now - started) / period) % roster.length : 0;
  const character = roster[index];
  const accent = character?.palette.accent ?? "#4aa7ff";
  drawStage(ctx, width, height, freq, accent, now);
  if (!character) return;
  const fade = Math.min(1, ((now - started) % period) / 280);
  drawFighter(ctx, character, sprites, (now - started) % period, width, height, fade);
  return character;
}

function drawStage(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  freq: Uint8Array,
  accent: string,
  now: number
): void {
  const bass = bandEnergy(freq, 1, 6);
  const mid = bandEnergy(freq, 10, 36);
  const high = bandEnergy(freq, 40, 80);
  const { r, g, b } = hexToRgb(accent);
  ctx.fillStyle = `rgba(0, 0, 8, ${0.22 + bass * 0.2})`;
  ctx.fillRect(0, 0, width, height);

  const glow = ctx.createRadialGradient(
    width / 2,
    height * 0.62,
    8,
    width / 2,
    height * 0.62,
    width * (0.22 + bass * 0.55)
  );
  glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.18 + bass * 0.55})`);
  glow.addColorStop(0.45, `rgba(${r}, ${g}, ${b}, ${0.05 + mid * 0.18})`);
  glow.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  const bars = 28;
  const gap = width / bars;
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.28 + high * 0.45})`;
  for (let i = 0; i < bars; i += 1) {
    const bin = freq[4 + i * 3] ?? 0;
    const level = (bin / 255) * height * (0.18 + bass * 0.55);
    ctx.fillRect(i * gap + 2, height - level, Math.max(2, gap - 4), level);
  }

  ctx.strokeStyle = `rgba(180, 220, 255, ${0.25 + mid * 0.55})`;
  ctx.lineWidth = 2;
  ctx.beginPath();
  const samples = 64;
  for (let i = 0; i <= samples; i += 1) {
    const bin = freq[8 + (i % 48)] ?? 0;
    const x = (i / samples) * width;
    const y = height * 0.58 + Math.sin(i * 0.35 + now / 280) * (8 + mid * 18) - (bin / 255) * 46;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();

  if (bass > 0.62 && now - lastPulse > 180) {
    pulses.push({ born: now, r, g, b });
    lastPulse = now;
    if (pulses.length > 8) pulses.shift();
  }
  for (const pulse of pulses) {
    const age = (now - pulse.born) / 900;
    if (age >= 1) continue;
    ctx.beginPath();
    ctx.strokeStyle = `rgba(${pulse.r}, ${pulse.g}, ${pulse.b}, ${1 - age})`;
    ctx.lineWidth = 2;
    ctx.ellipse(
      width / 2,
      height * 0.72,
      width * 0.12 * (1 + age * 3.2),
      height * 0.05 * (1 + age * 2.4),
      0,
      0,
      Math.PI * 2
    );
    ctx.stroke();
  }
  while (pulses.length && now - (pulses[0]?.born ?? now) > 900) pulses.shift();
}

function drawFighter(
  ctx: CanvasRenderingContext2D,
  character: CharacterDefinition,
  sprites: Map<string, HTMLImageElement>,
  now: number,
  width: number,
  height: number,
  alpha: number
): void {
  const sheet = character.spriteSheet;
  const image = sprites.get(character.id);
  if (!sheet || !image || !image.complete || image.naturalWidth === 0) return;
  const idle = sheet.animations.idle;
  if (!idle) return;
  const frame = animationFrameAt(idle, now / 1000);
  const column = frame % COLUMNS;
  const row = Math.floor(frame / COLUMNS);
  const size = height * 0.84;
  const x = (width - size) / 2;
  const y = height - size * 0.94;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = false;
  ctx.shadowBlur = 0;
  ctx.drawImage(image, column * FRAME, row * FRAME, FRAME, FRAME, x, y, size, size);
  ctx.restore();
}

const pulses: { born: number; r: number; g: number; b: number }[] = [];
let lastPulse = 0;

function bandEnergy(freq: Uint8Array, start: number, end: number): number {
  let total = 0;
  let count = 0;
  for (let index = start; index <= end; index += 1) {
    total += freq[index] ?? 0;
    count += 1;
  }
  return count ? total / count / 255 : 0;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const raw = hex.replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((char) => char + char).join("") : raw;
  const num = Number.parseInt(full, 16);
  if (!Number.isFinite(num)) return { r: 74, g: 167, b: 255 };
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function warmSprite(character: CharacterDefinition, sprites: Map<string, HTMLImageElement>): void {
  const url = character.spriteSheet?.image;
  if (!url || sprites.has(character.id)) return;
  const image = new Image();
  image.src = url;
  sprites.set(character.id, image);
}

function fitCanvas(canvas: HTMLCanvasElement): void {
  const width = Math.max(1, Math.floor(canvas.clientWidth * devicePixelRatio));
  const height = Math.max(1, Math.floor(canvas.clientHeight * devicePixelRatio));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
}

function createFader(parent: HTMLElement, label: string, onInput: () => void): HTMLElement {
  const fader = document.createElement("div");
  fader.className = "wa-fader";
  fader.tabIndex = 0;
  fader.setAttribute("role", "slider");
  fader.setAttribute("aria-label", label);
  fader.setAttribute("aria-valuemin", "-12");
  fader.setAttribute("aria-valuemax", "12");
  fader.dataset.db = "0";
  const track = document.createElement("div");
  track.className = "wa-fader-track";
  const thumb = document.createElement("div");
  thumb.className = "wa-fader-thumb";
  const caption = document.createElement("span");
  caption.textContent = label;
  fader.append(track, thumb, caption);
  parent.append(fader);
  setFader(fader, 0);

  const updateFromPointer = (event: PointerEvent): void => {
    const bounds = track.getBoundingClientRect();
    const ratio = bounds.height <= 0 ? 0.5 : (event.clientY - bounds.top) / bounds.height;
    setFader(fader, 12 - Math.min(1, Math.max(0, ratio)) * 24);
    onInput();
  };
  fader.addEventListener("pointerdown", (event) => {
    fader.setPointerCapture(event.pointerId);
    updateFromPointer(event);
  });
  fader.addEventListener("pointermove", (event) => {
    if (!fader.hasPointerCapture(event.pointerId)) return;
    updateFromPointer(event);
  });
  fader.addEventListener("keydown", (event) => {
    const current = Number(fader.dataset.db ?? 0);
    if (event.key === "ArrowUp") setFader(fader, current + 1);
    if (event.key === "ArrowDown") setFader(fader, current - 1);
    if (event.key === "Home") setFader(fader, 12);
    if (event.key === "End") setFader(fader, -12);
    onInput();
  });
  return fader;
}

function setFader(fader: HTMLElement | undefined, db: number): void {
  if (!fader) return;
  const clamped = Math.max(-12, Math.min(12, db));
  fader.dataset.db = String(clamped);
  fader.setAttribute("aria-valuenow", clamped.toFixed(0));
  const thumb = fader.querySelector<HTMLElement>(".wa-fader-thumb");
  if (thumb) thumb.style.top = `calc(${((12 - clamped) / 24) * 100}% - 6px)`;
}

function readFader(fader: HTMLElement | undefined): number {
  return Number(fader?.dataset.db ?? 0);
}

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`The soundtrack player is missing ${selector}.`);
  return element;
}

interface AudioGraph {
  context: AudioContext;
  pre: GainNode;
  level: GainNode;
  analyser: AnalyserNode;
  filters: BiquadFilterNode[];
}
